import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { styled } from '@mui/material/styles';
import { Box, Dialog, DialogContent, LinearProgress } from '@mui/material';
import { useForm } from 'react-hook-form';
import type {
  SyncedTripLegWithCareLevel,
  TripLeg,
} from '../../../../data/models/reservation.model';

import * as providerSlice from '../../../../data/provider.slice';
import * as triplegSlice from '../../../../data/tripleg.slice';
import * as userSlice from '../../../../data/user.slice';

import triplegUtils from '../../../../services/triplegUtils';

import { useAppSelector } from '../../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';

import { FullscreenDialogHeader } from '../../../dialogs/FullscreenDialogHeader';
import { ExceptionResolveForm } from './ExceptionResolveForm';

const PREFIX = 'ExceptionResolveDialog';

const classes = {
  content: `${PREFIX}-content`,
  pages: `${PREFIX}-pages`,
};

const Root = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    paddingLeft: 0,
    paddingRight: 0,
  },
  [`& .${classes.pages}`]: {
    display: 'flex',
    gap: theme.spacing(3),
    paddingLeft: theme.spacing(18),
  },
}));

interface ExceptionResolveDialogProps {
  tripLeg: SyncedTripLegWithCareLevel;
  open: boolean;
  close: () => void;
  userRoleBC: string;
  isMemberContactException?: boolean;
  showContactIcons?: boolean;
}

const ExceptionResolveDialog = ({
  tripLeg,
  open,
  close,
  userRoleBC,
  isMemberContactException,
  showContactIcons,
}: ExceptionResolveDialogProps) => {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();

  const isSaving = false;

  const tripLegsInReservation = useAppSelector(
    triplegSlice.selectAllTripLegsInReservation,
  );
  const loadingAllTripLegsInReservation = useAppSelector(
    triplegSlice.selectLoadingAllTripLegsInReservation,
  );
  const loggedInUserInfo = useAppSelector(userSlice.selectLoggedInUserInfo);
  const provider = useAppSelector(providerSlice.selectProvider);

  const subTitle = tripLeg.tripLegReferenceID ?? '';
  const title = t('exceptionResolve.title');

  const isLoading = loadingAllTripLegsInReservation;

  // TODO: get call outcomes from backend
  const callOutcomes = [
    {
      callOutcomeBC: 'MEMBER_UPDATED',
      callOutcomeName: 'Member Updated',
      role: ['MER'],
    },
    {
      callOutcomeBC: 'PROVIDER_WILL_UPDATE',
      callOutcomeName: 'Provider Will Update',
      isEscalated: false,
      role: ['PR1'],
    },
    {
      callOutcomeBC: 'NO_ANSWER',
      callOutcomeName: 'No Answer',
      isEscalated: false,
      role: ['PR1', 'MER'],
      childrenSelection: 'multiple',
      children: [
        {
          callOutcomeBC: 'LEFT_VOICEMAIL',
          callOutcomeName: 'Left Voicemail',
        },
      ],
    },
    {
      callOutcomeBC: 'ESCALATE',
      callOutcomeName: 'Escalate to Supervisor',
      role: ['MER'],
    },
    {
      callOutcomeBC: 'PROVIDER_REJECTED',
      callOutcomeName: 'Provider Rejected',
      isEscalated: false,
      role: ['PR1'],
      childrenSelection: 'single',
      childrenSelectionLabel: 'Reason',
      children: [
        {
          callOutcomeBC: 'PROVIDER_REJECTED_REASON_1',
          callOutcomeName: 'Provider Rejected Reason 1',
        },
        {
          callOutcomeBC: 'PROVIDER_REJECTED_REASON_2',
          callOutcomeName: 'Provider Rejected Reason 2',
        },
      ],
    },
    {
      callOutcomeBC: 'UNASSIGN',
      callOutcomeName: 'Unassign',
      isEscalated: true,
      role: ['PR2'],
      childrenSelection: 'single',
      childrenSelectionLabel: 'Reason',
      children: [
        {
          callOutcomeBC: 'NO_DRIVER_AVAILABLE',
          callOutcomeName: 'No Driver Available',
        },
        {
          callOutcomeBC: 'NO_DRIVER_ASSIGNED',
          callOutcomeName: 'No Driver Assigned',
        },
      ],
    },
  ];

  const formHook = useForm({
    mode: 'onTouched',
    defaultValues: {
      resolveReason: '',
      reasonOptions: {},
    },
  });

  useEffect(() => {
    // This effect handles selecting and deselecting parent/child field groups
    const subscription = formHook.watch((data, { name, type }) => {
      if (name?.startsWith('reasonOptions.')) {
        const parentCallOutcome = callOutcomes.find((callOutcome) =>
          callOutcome.children?.some(
            (child) =>
              child.callOutcomeBC === name.replace('reasonOptions.', ''),
          ),
        );
        if (parentCallOutcome) {
          formHook.setValue('resolveReason', parentCallOutcome.callOutcomeBC);
        }
      } else if (name === 'resolveReason') {
        callOutcomes.forEach((callOutcome) => {
          if (callOutcome.callOutcomeBC !== data.resolveReason) {
            callOutcome.children?.forEach((child) => {
              if (
                (data.reasonOptions as Record<string, boolean>)?.[
                  child.callOutcomeBC as string
                ] === true
              ) {
                formHook.setValue(
                  `reasonOptions.${child.callOutcomeBC}` as
                    | 'resolveReason'
                    | 'reasonOptions',
                  false,
                );
              }
            });
          }
        });
      }
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [formHook.watch]);

  const handleClose = () => {
    formHook.reset();
    close();
  };

  const handleResolveFormSubmit = () => {
    const resolveReason = formHook.getValues('resolveReason');
    const reasonOptions = formHook.getValues('reasonOptions');
    const options = Object.entries(reasonOptions)
      .filter(([_key, value]) => value === true)
      .map(([key, _value]) => key);
    let optionsNames: string[] = [];
    callOutcomes.forEach((callOutcome) => {
      callOutcome.children?.forEach((child) => {
        if (options.includes(child.callOutcomeBC)) {
          optionsNames.push(child.callOutcomeName);
        }
      });
    });
    const note = `Exception resolved
reason: ${
      callOutcomes.find(
        (callOutcome) => callOutcome.callOutcomeBC === resolveReason,
      )?.callOutcomeName ?? ''
    }${optionsNames.length > 0 ? `, ${optionsNames.join(', ')}` : ''}
by: ${loggedInUserInfo.userFirstName!} ${loggedInUserInfo.userLastName!}
on: ${DateTime.now().toLocaleString(DateTime.DATETIME_SHORT)}`;
    dispatch(
      providerSlice.addInternalNoteAsync(tripLeg.tripLegReferenceID!, note),
    );
    dispatch(
      triplegSlice.clearTripLegExceptionAsync(
        tripLeg.tripLegReferenceID!,
        true,
      ),
    );
    dispatch(
      triplegSlice.clearTripEscalationAsync(tripLeg.tripLegReferenceID!),
    );
    /*
     * Based on the submitted call outcome, we need to perform the following actions:
     * - 'PROVIDER_WILL_UPDATE': Nothing to do
     * - 'NO_ANSWER': Nothing to do
     * - 'ESCALATE': Escalate the trip leg
     * - 'PROVIDER_REJECTED': Reject the trip leg
     * - 'UNASSIGN': Unassign the trip leg
     */
    switch (resolveReason) {
      case 'PROVIDER_WILL_UPDATE':
        break;
      case 'NO_ANSWER':
        break;
      case 'ESCALATE':
        handleEscalateTripLeg();
        break;
      case 'PROVIDER_REJECTED':
        handleUnassignTripLeg();
        break;
      case 'UNASSIGN':
        handleUnassignTripLeg();
        break;
    }
    handleClose();
  };

  /*
   * Call outcome selection actions
   */
  const handleEscalateTripLeg = () => {
    dispatch(triplegSlice.escalateTripLegAsync(tripLeg.tripLegReferenceID!));
  };

  const handleUnassignTripLeg = (reasonBC?: string) => {
    dispatch(
      triplegSlice.rejectTripLegAsync({
        passengerUUID: tripLeg.passengerUUID!,
        tripLegReferenceID: tripLeg.tripLegReferenceID!,
        tripLegRejectedReasonBC: reasonBC ?? 'UNASSIGN',
      }),
    );
  };

  // When this form is submitted, there are various pending actions to perform:
  // 1. Add an internal note to the trip leg to record the resolution
  // 2. Escalate the trip leg
  // 3. Unassign the trip leg
  // 4. Update the trip leg status
  // We need custom actions to ensure all the pieces resolve successfully before
  // closing the dialog.
  // useEffect(() => {
  // }, []);

  useEffect(() => {
    dispatch(providerSlice.setProvider(null));

    // Fetch provider info to display contact info
    if (tripLeg.transportationProviderID) {
      dispatch(
        providerSlice.fetchProviderAsync(tripLeg.transportationProviderID),
      );
    }

    // Check if the current assignee is the same as the current user
    // if not, assign ourselves to the trip leg
    const exceptionAssignmentUserID = triplegUtils.getExceptionAssignmentUserID(
      tripLeg as unknown as TripLeg,
    );
    if (exceptionAssignmentUserID !== loggedInUserInfo.userAccountID!) {
      dispatch(
        triplegSlice.setTripLegExceptionAssignmentAsync(
          tripLeg.tripLegReferenceID!,
          loggedInUserInfo?.userAccountID!,
        ),
      );
    }
  }, []);

  return (
    <Root fullScreen open={open} onClose={handleClose}>
      <FullscreenDialogHeader
        title={title}
        subTitle={subTitle}
        close={handleClose}
      />
      {isLoading && <LinearProgress />}
      <DialogContent className={classes.content}>
        <Box className={classes.pages}>
          <ExceptionResolveForm
            formHook={formHook}
            isSaving={isSaving}
            isLoading={isLoading}
            tripLeg={tripLeg}
            tripLegsInReservation={tripLegsInReservation}
            provider={provider}
            submit={formHook.handleSubmit(handleResolveFormSubmit)}
            userRoleBC={userRoleBC}
            close={handleClose}
            callOutcomes={callOutcomes}
            isMemberContactException={isMemberContactException}
            showContactIcons={showContactIcons}
          />
        </Box>
      </DialogContent>
      {isLoading && <LinearProgress />}
    </Root>
  );
};

export { ExceptionResolveDialog };
